import { MenuCategory } from "./menu.category.model";
import { MenuCategoryBuilder } from "./menu.category.builder";
import { environment } from "src/environments/environment";
import { BehaviorSubject, of } from "rxjs";
import { catchError, map, switchMap, take } from "rxjs/operators";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { ToastService } from "ng-uikit-pro-standard";
import { AuthService } from "../../services";

export class MenuCategoryFactory {
  public categories$: BehaviorSubject<MenuCategory[]>;
  public builder: MenuCategoryBuilder;  

  constructor(
    private venueId: string,
    private toast: ToastService,
    private httpClient: HttpClient,
    private authService: AuthService,
  ) {
    this.categories$ = new BehaviorSubject([]);
    this.getCategoriesOnInitialization();
  }

  public async getCategoriesOnInitialization(): Promise<void> {
    const userJWT = await this.authService.user.getIdToken();

    this.httpClient.get<MenuCategory[]>(`${environment.apiBaseUrl}/api/v1/categories/${this.venueId}`, {
        headers: new HttpHeaders().set("Authorization", `${userJWT}`),
      }).subscribe((categoriesResponse) => {
        const categories = categoriesResponse
          .map(categoryResponse => new MenuCategoryBuilder().loadAPIResponse(categoryResponse).build())
          .sort((a, b) => a.position - b.position);
  
        this.categories$.next(categories);
      })
  }

  public create = () => (this.builder = new MenuCategoryBuilder());

  public toggleIsActive = async (category: MenuCategory) => {
    this.fix({
      ...category,
      isActive: !category.isActive,
    } as MenuCategory);
    
    await this._update();
  }

  public updatePosition = async (category: MenuCategory, updatedPosition: number) => {
    this.fix({
      ...category,
      position: updatedPosition,
    } as MenuCategory);

    await this._update();
  }

  public remove = (category: MenuCategory) =>
    this._delete(category.id);

  public fix = (category: MenuCategory) =>
    (this.builder = new MenuCategoryBuilder().edit(category));

  public async deploy() {
    this.builder.id !== "" ? await this._update() : await this._add();
  }

  private _clean = () => (this.builder = null);

  private async _add() {
    // get the currentCategories to keep the current state in the event of an error.
    const currentCategories = this.categories$.getValue();
    const userJWT = await this.authService.user.getIdToken();

    await this.categories$.pipe(
      take(1),
      switchMap(categories => this.httpClient.post(`${environment.apiBaseUrl}/api/v1/categories`, {
        venueId: this.venueId,
        data: this.builder.toAPIResponse(),
      }, {
        headers: new HttpHeaders().set("Authorization", `${userJWT}`),
      }).pipe(
        map((createCategoryResponse) => {
          return [...categories, new MenuCategoryBuilder().loadAPIResponse(createCategoryResponse).build()];
        }),
        catchError((err) => {
          this.toast.error(`Could not create category: ${this.builder.name}`, "Error", {
            opacity: 1,
          });
          return of(currentCategories);
        })
      ))
    ).subscribe(categories => this.categories$.next(categories));
  }

  private async _update() {
    // get the currentCategories to keep the current state in the event of an error.
    const currentCategories = this.categories$.getValue();
    const userJWT = await this.authService.user.getIdToken();

    await this.categories$.pipe(
      take(1),
      switchMap(categories => this.httpClient.put(`${environment.apiBaseUrl}/api/v1/categories`, {
        id: this.builder.id,
        venueId: this.venueId,
        data: this.builder.toAPIResponse(),
      }, {
        headers: new HttpHeaders().set("Authorization", `${userJWT}`),
      }).pipe(
        map((updateCategoryResponse) => {
          const updatedCategory = new MenuCategoryBuilder().loadAPIResponse(updateCategoryResponse).build();
          return categories.map(category => {
            return category.id === updatedCategory.id ?
              updatedCategory :
              category;
          })
        }),
        catchError((err) => {
          this.toast.error(`Could not update category: ${this.builder.name}`, "Error", {
            opacity: 1,
          });
          return of(currentCategories);
        })
      ))
    ).subscribe(categories => this.categories$.next(categories));
  }

  private async _delete(id: string) {
    // get the currentCategories to keep the current state in the event of an error.
    const currentCategories = this.categories$.getValue();
    const userJWT = await this.authService.user.getIdToken();

    this._clean();
    await this.categories$.pipe(
      take(1),
      switchMap(categories => this.httpClient.delete(`${environment.apiBaseUrl}/api/v1/categories/${this.venueId}/${id}`, {
        headers: new HttpHeaders().set("Authorization", `${userJWT}`),
      }).pipe(
        map(() => {
          return categories.filter(category => category.id !== id)
        }),
        catchError((err) => {
          this.toast.error("Could not delete category.", "Error", {
            opacity: 1,
          });
          return of(currentCategories);
        })
      ))
    ).subscribe(categories => this.categories$.next(categories));
  }

  public getDescription(categories: MenuCategory[]): string {
    return categories.length != 0
      ? "" +
          categories.length +
          "(" +
          categories.reduce(
            (acc, category, index) =>
              (acc +=
                category.name + (index != categories.length - 1 ? ", " : "")),
            ""
          ) +
          ")"
      : "None";
  }
}
